import React from 'react';
import { Cloud } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import './ThoughtBubbles.css';
import footerLogo from '../assets/Footerimg.png';

// Import real screenshots with EXACT filenames from uploads
import step1Img from '../assets/step one 1.png';
import step2Img from '../assets/Step two 2.jpg';
import step3Img from '../assets/Step three  3.jpg';
import step4Img from '../assets/Image1_Dashboard.png';
import step5Img from '../assets/Step five 5.jpg';
import step6Img from '../assets/Image2_Dashboard.png';
import step7Img from '../assets/Step 7 .jpg';

import img1 from '../assets/main2.png';

// Consolidated to 3 key steps with real screenshots
const tutorialSteps = [
  {
    number: "1",
    title: "Copy & Enter Job Details",
    description:
      "Navigate to your dream company's careers page and copy the URL. Then enter the specific job title you're looking for (e.g., 'Chief Financial Officer'). Then set the frequency that you want the Search to Happen.",
    image: step4Img, // Shows the filled form
  },
  {
    number: "2",
    title: "Choose Search Frequency",
    description:
      "Now that you have selected the frequency you will be able to track the jobs being sorted in color-coded pills: Dark Green for exact matches, Light Green for Partial Matches and Red for No Matches. If the results aren't found Jobque will monitor them for you.",
    image: step6Img, // Shows frequency selection
  },
  {
    number: "3",
    title: "Apply Promptly and Confidently",
    description:
      "Receive job alerts directly to your email and click through to apply instantly. This proactive approach significantly increases your chances of becoming one of the first applicants.",
    image: step7Img, // Shows email notification
  },
];

function ThoughtBubbles() {
  const navigate = useNavigate();

  const handleOpenTool = () => {
    localStorage.setItem('selectedTab', 'Tool');
    navigate('/tool');
  };

  return (
    <div className="page-container">
      <div className="content-wrap">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <h1>Be First in Line for Your Dream Job</h1>
            <p className="hero-subtitle">
              Get notified instantly when positions open at your dream companies.
              Don't be the 153rd applicant - be among the first.
            </p>
            <button className="cta-button primary-btn" onClick={handleOpenTool}>
              Try JobQue.ai
            </button>
          </div>
          <div className="hero-image">
            <img src={img1} alt="Job search illustration" />
          </div>
        </section>

        {/* Problem Section */}
        <section className="problem-section">
          <div className="section-header">
            <h2>The Job Search Problem</h2>
            <p>Most candidates apply too late, after hundreds of others already have</p>
          </div>

          {/* NEW: Equation-style layout */}
          <div className="problem-equation" data-aos="fade-up">
            <div className="problem-card">
              <h3>Manual Work</h3>
              <p>"I check career pages manually every day — it's exhausting."</p>
            </div>

            <span className="equation-operator" aria-hidden="true">+</span>

            <div className="problem-card">
              <h3>Too Late</h3>
              <p>"Every time I apply, I'm already the 153rd applicant."</p>
            </div>

            <span className="equation-operator" aria-hidden="true">+</span>

            <div className="problem-card">
              <h3>Biased Monitoring</h3>
              <p>
                "It favors people who constantly monitor and apply first."
              </p>
            </div>

            <span className="equation-operator equals" aria-hidden="true">=</span>

            <div className="problem-card">
              <h3>Missing Opportunities</h3>
              <p>"How am I supposed to know when a job is actually available?"</p>
            </div>

            <span className="equation-operator" aria-hidden="true">+</span>

            <div className="problem-card">
              <h3>Frustration</h3>
              <p>"Why do others always find jobs before I even know they exist?"</p>
            </div>
          </div>
        </section>

        {/* How It Works Section - HORIZONTAL WITH REAL SCREENSHOTS */}
        <section className="how-it-works-section">
          <div className="section-header">
            <h2>How JobQue.ai Works</h2>
            <p>Simple, automated, and effective job hunting</p>
          </div>

          <div className="horizontal-steps-container">
            {tutorialSteps.map((step, index) => (
              <div
                className="horizontal-step-card"
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="step-number-badge">{step.number}</div>
                <div className="step-screenshot">
                  <img src={step.image} alt={step.title} />
                </div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="testimonials-section">
          <div className="section-header">
            <h2>What Our Users Say</h2>
            <p>
              Join thousands of job seekers who've secured interviews through early
              applications
            </p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card" data-aos="fade-up">
              <p>
                "Thanks to JobQue, I was the 3rd applicant for my current role. The
                hiring manager told me they were impressed by how quickly I found the
                posting."
              </p>
              <div className="testimonial-author">
                <img
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150"
                  alt="Profile"
                  className="author-image"
                />
                <span>Sarah J.</span>
              </div>
            </div>

            <div className="testimonial-card" data-aos="fade-up" data-aos-delay="100">
              <p>
                "After months of applying to positions with hundreds of applicants, JobQue
                helped me be among the first 10 for my dream job. I got the interview
                within 24 hours."
              </p>
              <div className="testimonial-author">
                <img
                  src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"
                  alt="Profile"
                  className="author-image"
                />
                <span>Michael T.</span>
              </div>
            </div>

            <div className="testimonial-card" data-aos="fade-up" data-aos-delay="200">
              <p>
                "No more manually checking career pages every day. JobQue does the work
                and I just wait for the email. It's changed my job search completely."
              </p>
              <div className="testimonial-author">
                <img
                  src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150"
                  alt="Profile"
                  className="author-image"
                />
                <span>Ava R.</span>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="final-cta-section">
          <div className="final-cta-content">
            <h2>Be Among the First Applicants</h2>
            <p>
              Don't miss out on your next career opportunity. Start monitoring your dream
              companies today.
            </p>
            <button className="cta-button secondary-btn" onClick={handleOpenTool}>
              Start Using JobQue Now
            </button>
          </div>
          <div className="cta-image">
            <img src={footerLogo} alt="JobQue Logo" className="cta-logo" />
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default ThoughtBubbles;
